import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiFileText, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import { handleUnauthorized } from '../../utils/apiClient';
import '../commissionscan/CommissionScan.css';
import '../../styles/DataQualityScanUpload.css';
const getToolboxToken = (): string => localStorage.getItem('toolboxToken') || '';

interface Props {
  onGoToHistory?: () => void;
}

const DataQualityScanUpload: React.FC<Props> = ({ onGoToHistory }) => {
  const [file,           setFile]           = useState<File | null>(null);
  const [isDragging,     setIsDragging]     = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [errorMessage,   setErrorMessage]   = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pickFile = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!/\.csv$/i.test(f.name)) {
      setErrorMessage('Please upload a CSV file only.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    setFile(f);
    setSuccessMessage(false);
    setErrorMessage('');
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const handleCancel = () => {
    setFile(null);
    setSuccessMessage(false);
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const upload = () => {
    if (!file) return;
    setLoading(true);
    setErrorMessage('');
    const formData = new FormData();
    formData.append('file', file);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/DataQuality/dataimport`, formData, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(() => {
        setLoading(false);
        setFile(null);
        setSuccessMessage(true);
      })
      .catch((err: any) => {
        setLoading(false);
        if (err.response?.status === 401) {
          handleUnauthorized();
        } else {
          setErrorMessage('Error while uploading the file. Please try again.');
          setTimeout(() => setErrorMessage(''), 4000);
        }
      });
  };

  const canScan = !loading && !!file;

  return (
    <div className="cs-main data-quality-scan-upload__main-flush">
      {/* Content */}
      <div className="cs-content">
        {successMessage ? (
          <div className="cs-success-banner-wrap">
            <div className="cs-success-banner">
              <div className="cs-success-banner__icon">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="cs-success-banner__body">
                <p className="cs-success-banner__title">File uploaded successfully!</p>
                <p className="cs-success-banner__desc">
                  We are processing your file. Please check the{' '}
                  <button className="cs-success-link" onClick={onGoToHistory}>History</button>
                  {' '}tab to track progress.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
          >
            {!file ? (
              <>
                <div className="cs-dropzone__icon">
                  <FiFileText size={28} />
                </div>
                <p className="cs-dropzone__title">Drag &amp; drop your CSV file here</p>
                <p className="cs-dropzone__or">or</p>
                <motion.button
                  className="cs-browse-btn"
                  onClick={() => fileInputRef.current?.click()}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Browse Files
                </motion.button>
                <p className="cs-dropzone__types">
                  Accepted format: <strong>CSV</strong>
                </p>
              </>
            ) : (
              <div className="cs-file-card">
                <div className="cs-file-card__icon">
                  <FiFileText size={20} />
                </div>
                <div className="cs-file-card__info">
                  <span className="cs-file-card__name">{file.name}</span>
                  <span className="cs-file-card__size">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                </div>
                <button
                  className="cs-file-card__delete"
                  onClick={() => setFile(null)}
                  title="Remove file"
                >
                  <FiTrash2 size={15} />
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="data-quality-scan-upload__hidden-input"
              onChange={e => pickFile(e.target.files)}
            />
          </div>
        )}
      </div>

      {/* Info banner */}
      {!successMessage && (
        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>Upload a CSV file containing policy data to compare against your NowCerts AMS records.</span>
        </div>
      )}

      {/* Footer */}
      <div className="cs-footer">
        <span className="cs-footer__status">
          {errorMessage ? (
            <span className="data-quality-scan-upload__error-text">{errorMessage}</span>
          ) : !successMessage && (
            !file ? 'No file added yet.' : `${file.name} ready to scan`
          )}
        </span>
        <div className="cs-footer__actions">
          {successMessage ? (
            <motion.button
              className="cs-btn-start"
              onClick={handleCancel}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              ✦ New Scan
            </motion.button>
          ) : (
            <>
              <button className="cs-btn-cancel" onClick={handleCancel} disabled={loading}>
                Cancel
              </button>
              <motion.button
                className="cs-btn-start"
                disabled={!canScan}
                whileHover={{ scale: canScan ? 1.03 : 1 }}
                whileTap={{ scale: canScan ? 0.97 : 1 }}
                onClick={upload}
              >
                {loading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
              </motion.button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataQualityScanUpload;

